import styled from '@emotion/styled';
import { useFlags } from 'launchdarkly-react-client-sdk';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps } from 'react-router-dom';
import { toastNotificationsService } from 'src/config/rootService';

import { Dispatch } from 'src/config/rootStore';
import { TopicOfFocusNode } from 'src/generated/webapp_gql';
import { useGetTopicsOfFocusQuery } from 'src/modules/goals/documents/getTopicsOfFocusQuery.generated';
import { useSelectTopicsOfFocusMutation } from 'src/modules/goals/documents/selectTopicsOfFocusMutation.generated';
import { getMainGoalCategoriesWithSubcategories } from 'src/modules/goals/selectors/goalsSelectors';
import { getUserInformation } from 'src/modules/user/selectors/userSelectors';
import questionIcon from 'src/public/img/icon-question-grey.svg';
import { useLocalizedVersion } from 'src/shared/hooks/useLocalizedVersion';
import { useOnboardingGuard } from 'src/shared/hooks/useOnboardingGuard';
import { color, font, mediaQueries } from 'src/shared/styles/variables';

import { BodyTextMedium, ButtonGroup, HeadingText50, mediaQuery } from 'src/shared/ds';
import { pxToRem } from 'src/shared/helpers/pxToRem';
import { BrandWrapper } from '../components/BrandWrapper';
import { CategorySelectBox } from '../components/CategorySelectBox';
import { InformationModal } from '../components/InformationModal';

const Wrapper = styled.div({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    position: 'absolute',
    top: '20%',
    textAlign: 'center',
    width: '100%',
    padding: '1em',
    [mediaQueries.sm]: {
        top: '8%',
    },
});

const TextsWrapper = styled.div({
    position: 'relative',
    display: 'inline-block',
    padding: '0 15px',
});

const ContentWrapper = styled.div({
    width: '30%',
    marginTop: pxToRem(40),

    [mediaQuery.maxWidth('md')]: {
        minWidth: '90%',
    },
    [mediaQuery.minWidth('md')]: {
        minWidth: pxToRem(450),
    },
});

const QuestionIcon = styled.img({
    display: 'inline-block',
    position: 'absolute',
    paddingLeft: 10,
    cursor: 'pointer',
});

const CategoriesList = styled.ul({
    display: 'flex',
    flexWrap: 'wrap',
    maxWidth: 900,
    paddingBottom: '2em',
    paddingLeft: 0,
    justifyContent: 'space-around',
    margin: '0 auto',
});

interface CategoryProps {
    active?: boolean;
    disabled?: boolean;
}

const Category = styled.li<CategoryProps>(props => ({
    opacity: props.disabled ? 0.6 : 1,
    position: 'relative',
    width: '48%',
    padding: '2em',
    border: '2.5px solid',
    borderColor: props.active ? color.cornflowerBlue : color.linkWaterLight,
    borderRadius: 6,
    marginBottom: '1em',
    cursor: 'pointer',
    textAlign: 'left',
    transition: '0.3s all',
    '&:hover': {
        borderColor: props.active ? color.cornflowerBlue : '#adc0f2',
    },
    [mediaQueries.md]: {
        width: '80%',
    },
    [mediaQueries.sm]: {
        width: '100%',
    },
}));

const CategoryName = styled.p(
    {
        color: color.fiord,
    },
    font.heavy(font.size.medium),
);

const CategoryImage = styled.img({
    position: 'absolute',
    width: 150,
    bottom: 0,
    right: 0,
});

const NewCategoryListWrapper = styled.div({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    marginBottom: pxToRem(24),
});

type Props = RouteComponentProps;

export enum OnboardingCategoriesStepPageTestId {
    DeprecatedCategoryList = 'OnboardingCategoriesStepPageTestId.DeprecatedCategoryList',
    CategoryList = 'OnboardingCategoriesStepPageTestId.CategoryList',
    Continue = 'OnboardingCategoriesStepPageTestId.Continue',
    DeprecatedCategoryBox = 'OnboardingCategoriesStepPageTestId.DeprecatedCategoryBox',
}

export const OnboardingCategoriesStepPage: React.FC<Props> = ({ history }) => {
    const flags = useFlags();

    const { t } = useTranslation();

    const shouldRender = useOnboardingGuard();
    const dispatch: Dispatch = useDispatch();

    const categories = useSelector(getMainGoalCategoriesWithSubcategories);
    const { careRecommendations } = useSelector(getUserInformation);

    const [selectedCategories, setSelectedCategories] = useState<number[]>([]);
    const [saveInProgress, setSaveInProgress] = useState(false);
    const [informationModalVisible, setInformationModalVisible] = useState(false);
    const [localizedVersion] = useLocalizedVersion();

    const showNewCategories = !flags?.hideRevampedTriageFlowPart2 && !localizedVersion;

    const { data, loading } = useGetTopicsOfFocusQuery();
    const [selectTopicsOfFocus] = useSelectTopicsOfFocusMutation();

    const toggleModalVisibility = () => setInformationModalVisible(!informationModalVisible);

    const [dropDownState, setDropDownState] = useState({});

    useEffect(() => {
        if (!data?.topicsOfFocus) {
            return;
        }

        const obj = {};

        data?.topicsOfFocus.forEach(item => {
            obj[item?.uuid] = {
                active: false,
                name: item?.name,
                id: item?.uuid,
                selectedItems: [],
            };
        });
        setDropDownState(obj);
    }, [loading, data]);

    const newCategories = Object.keys(dropDownState)
        .filter(key => dropDownState[key].selectedItems.length > 0)
        .map(key => ({
            name: dropDownState[key].name,
            id: dropDownState[key].id,
            subcategories: dropDownState[key].selectedItems,
        }));

    const handleCategoryClick = (categoryId: number) => () => {
        const alreadySelected = selectedCategories.includes(categoryId);

        if (saveInProgress) {
            return;
        }

        // can't select more than 3
        if (!alreadySelected && selectedCategories.length === 3) {
            return;
        }

        const newSelectedCategories = alreadySelected
            ? selectedCategories.filter(id => id !== categoryId)
            : [...selectedCategories, categoryId];

        setSelectedCategories(newSelectedCategories);
    };

    const handleSubmit = async () => {
        setSaveInProgress(true);

        if (showNewCategories) {
            const nextTopicSelections = Object.keys(dropDownState)
                .filter(key => {
                    return dropDownState[key].selectedItems.length > 0;
                })
                .map(key => {
                    return {
                        topicUuid: key,
                        subtopicUuids: dropDownState[key].selectedItems,
                    };
                });

            await selectTopicsOfFocus({
                variables: {
                    careRecommendationUuid: careRecommendations[0].uuid,
                    topicSelections: nextTopicSelections,
                },
            });

            // the backend handle mapping the new topics to the old ones so we
            // want to just go refetch them now that they have been updated in the db
            await dispatch.goalInterests.fetchGoalInterests(newCategories);
            history.push(flags?.hideTriageSymptoms ? '/a-few-questions' : '/onboarding-wellness');
        } else {
            await dispatch.goalInterests.saveGoalInterests(selectedCategories);

            history.push('/a-few-questions');
        }

        setSaveInProgress(false);
    };

    const handleOnClickSelectBox = (id: number) => {
        const next = {};
        Object.keys(dropDownState).map(key => {
            next[key] = {
                ...dropDownState[key],
                active: false,
            };
        });

        setDropDownState({
            ...next,
            [id]: {
                ...dropDownState[id],
                active: !dropDownState[id].active,
            },
        });
    };

    const handleOnClickOutsideSelectBox = (id: number) => {
        setDropDownState({
            ...dropDownState,
            [id]: {
                ...dropDownState[id],
                active: false,
            },
        });
    };

    const handleOnClickSubtopic = (categoryId: number, topic: string) => {
        const isAddingNewCategory = newCategories.find(cat => cat.id === categoryId);
        // validation bc user can't select more than 3 categories
        if (!isAddingNewCategory && newCategories.length === 3) {
            return;
        }

        // this refers to the subtopics a user has selected in the active dropdown
        const currentlySelectedSubTopics = dropDownState[categoryId].selectedItems;

        const everySelectedSubTopic = newCategories.flatMap(cat => cat.subcategories);
        // validation bc user can't select more than 3 subtopics
        if (everySelectedSubTopic.length === 3 && !everySelectedSubTopic.includes(topic)) {
            toastNotificationsService.renderErrorToast(
                t('onboarding:onboarding_categories_step_page.maximum_number_of_topics_selected'),
            );

            return;
        }

        // add or remove the topic a user selected to the selectedItems array for the active dropdown
        const nextTopics = currentlySelectedSubTopics.includes(topic)
            ? currentlySelectedSubTopics.filter(item => item !== topic)
            : [...currentlySelectedSubTopics, topic];

        // update the state of the dropdown
        setDropDownState({
            ...dropDownState,
            [categoryId]: {
                ...dropDownState[categoryId],
                selectedItems: nextTopics,
            },
        });
    };

    const isContinueDisabled = showNewCategories
        ? !newCategories.length
        : !selectedCategories.length;

    return shouldRender ? (
        <BrandWrapper>
            <Wrapper>
                <TextsWrapper>
                    <HeadingText50>
                        {t('onboarding:onboarding_categories_step_page.title')}
                        {!showNewCategories && (
                            <QuestionIcon src={questionIcon} onClick={toggleModalVisibility} />
                        )}
                    </HeadingText50>
                    <BodyTextMedium centered marginTop={6} fontColor="neutral400">
                        {t('onboarding:onboarding_categories_step_page.description')}
                    </BodyTextMedium>
                </TextsWrapper>
                <ContentWrapper>
                    {showNewCategories ? (
                        <NewCategoryListWrapper
                            data-testid={OnboardingCategoriesStepPageTestId.CategoryList}
                        >
                            {data?.topicsOfFocus &&
                                (data?.topicsOfFocus as TopicOfFocusNode[]).map(category => {
                                    return (
                                        <CategorySelectBox
                                            key={category?.uuid}
                                            name={category?.name || ''}
                                            image={category?.image || ''}
                                            active={dropDownState[category?.uuid]?.active}
                                            selectedItems={
                                                dropDownState[category?.uuid]?.selectedItems
                                            }
                                            subTopics={category?.subtopicsOfFocus}
                                            onClickOutsideBox={() =>
                                                handleOnClickOutsideSelectBox(category?.uuid)
                                            }
                                            onClickBox={() =>
                                                handleOnClickSelectBox(category?.uuid)
                                            }
                                            onClickSubtopic={subtopicUuid =>
                                                handleOnClickSubtopic(category?.uuid, subtopicUuid)
                                            }
                                        />
                                    );
                                })}
                        </NewCategoryListWrapper>
                    ) : (
                        <CategoriesList
                            data-testid={OnboardingCategoriesStepPageTestId.DeprecatedCategoryList}
                        >
                            {categories.map((category, i) => (
                                <Category
                                    data-testid={
                                        OnboardingCategoriesStepPageTestId.DeprecatedCategoryBox + i
                                    }
                                    key={category.id}
                                    active={selectedCategories.includes(category.id)}
                                    disabled={
                                        selectedCategories.length === 3 &&
                                        !selectedCategories.includes(category.id)
                                    }
                                    onClick={handleCategoryClick(category.id)}
                                >
                                    <CategoryName>{category.name}</CategoryName>
                                    <CategoryImage src={category.image} />
                                </Category>
                            ))}
                        </CategoriesList>
                    )}
                    <ButtonGroup
                        onFirstButtonClick={history.goBack}
                        onSecondButtonClick={handleSubmit}
                        secondButtonDisabled={isContinueDisabled}
                        firstButtonText={t('common.back')}
                        secondButtonText={t('common.continue')}
                        secondButtonTestId={OnboardingCategoriesStepPageTestId.Continue}
                    />
                </ContentWrapper>
            </Wrapper>
            <InformationModal
                visible={informationModalVisible}
                onToggle={toggleModalVisibility}
                goalCategories={categories}
            />
        </BrandWrapper>
    ) : null;
};
